import {
    AlertCircleOutline,
    CashOutline,
    CheckmarkCircleOutline,
    DocumentTextOutline,
    PhonePortraitOutline,
    TimeOutline,
  } from "@raresail/react-ionicons";
  import { useEffect } from "react";
  import useGet from "../../../hooks/common/useGet";
  import {
    API_DASHBOARD_BORROWER,
    API_DEVICE_ORDERS,
  } from "../../../constants/apis";
  import { currencyFormat, formatDate, stringify } from "../../../utils/helpers";
  import { SimpleLoader } from "../../common/Loader";
  import { Heading, StatusTag } from "../../elements/Elements";
  import { openBottomSheet, openModalForm } from "../../../utils/modalUtils";
  import {
    ACCEPT_OFFER,
    DEVICE_ORDERS,
    LOANS,
  } from "../../../constants/constants";
  import { primary5 } from "../../../constants/colors";
  import { getListRoute } from "../../../utils/helpers";
  import { Link } from "react-router-dom";
  import { OrderFooter } from "../../../hooks/devices/useDeviceOrdersList";
  
  export const BorrowerDashboardCard = () => {
    const { loadData, data, loading } = useGet();
    const {
      loadData: loadOrders,
      data: ordersData,
      loading: ordersLoading,
    } = useGet();
  
    useEffect(() => {
      loadData({ api: API_DASHBOARD_BORROWER });
      loadOrders({ api: API_DEVICE_ORDERS, params: { page_size: 3 } });
    }, []);
  
    if (loading) return <SimpleLoader />;
    if (!data) return null;
  
    const {
      active_loans = [],
      total_outstanding = 0,
      next_due_date,
      next_due_amount,
      loans_pending_offer = [],
    } = data;
  
    const hasActiveLoans = active_loans.length > 0;
    const hasPendingOffers = loans_pending_offer.length > 0;
    const deviceOrders = ordersData?.results || [];
    const hasDeviceOrders = deviceOrders.length > 0;
  
    return (
      <div className="flexColumn gap12">
        {/* ── Pending-offer nudge strips ── */}
        {hasPendingOffers &&
          loans_pending_offer.map((loan: any) => (
            <div
              key={loan.id}
              className="p12 r10 flexNullCenter gap10 pointer animate"
              style={{
                background: "#e8f4fd",
                border: "1px solid #90caf9",
              }}
              onClick={() =>
                openModalForm({ source: ACCEPT_OFFER, state: loan })
              }
            >
              <AlertCircleOutline fontSize={18} style={{ color: "#1976d2" }} />
              <div className="flexGrow">
                <div className="font12 semiBold textDark">
                  Loan offer ready to review
                </div>
                <div className="font11 grey">
                  {currencyFormat(loan.amount)} — tap to review & accept
                </div>
              </div>
              <div
                className="font11 semiBold r20"
                style={{
                  padding: "4px 12px",
                  background: "#1976d2",
                  color: "#fff",
                }}
              >
                Review
              </div>
            </div>
          ))}
  
        {/* ── Loan summary stats ── */}
        {hasActiveLoans && (
          <div
            className="r10 p15 flexColumn gap10 whiteBg"
            style={{ border: `1px solid ${primary5}` }}
          >
            <Heading className="font14">Active Loans</Heading>
  
            <div className="flexSpaceCenter">
              <div className="flexNullCenter gap6 grey font12">
                <CashOutline fontSize={14} />
                Outstanding balance
              </div>
              <div className="font13 semiBold red">
                {currencyFormat(total_outstanding)}
              </div>
            </div>
  
            {next_due_date && (
              <div className="flexSpaceCenter">
                <div className="flexNullCenter gap6 grey font12">
                  <TimeOutline fontSize={14} />
                  Next payment due
                </div>
                <div className="flexColumn textEnd">
                  <div className="font12 semiBold textDark">
                    {currencyFormat(next_due_amount)}
                  </div>
                  <div className="font11 grey">{formatDate(next_due_date)}</div>
                </div>
              </div>
            )}
  
            <div className="flexColumn gap8 mt5">
              {active_loans.map((loan: any) => (
                <ActiveLoanRow key={loan.id} loan={loan} />
              ))}
            </div>
          </div>
        )}
  
        {/* ── Device orders section ── */}
        {hasDeviceOrders && (
          <div
            className="r10 p15 flexColumn gap10 whiteBg"
            style={{ border: `1px solid ${primary5}` }}
          >
            <div className="flexSpaceCenter">
              <Heading className="font14">BNPL Orders</Heading>
              <Link
                to={getListRoute(DEVICE_ORDERS)}
                className="font12 main semiBold"
              >
                View All
              </Link>
            </div>
  
            <div className="flexColumn gap8">
              {deviceOrders.slice(0, 3).map((order: any) => (
                <DeviceOrderRow key={order.id} order={order} />
              ))}
            </div>
          </div>
        )}
  
        {!hasActiveLoans && !hasPendingOffers && !hasDeviceOrders && (
          <div
            className="font12 grey textCenter p20 r10 whiteBg"
            style={{ border: `1px solid ${primary5}` }}
          >
            You have no active loans or device orders. Apply for a loan or
            finance a device to get started.
          </div>
        )}
      </div>
    );
  };
  
  const ActiveLoanRow = ({ loan }: { loan: any }) => {
    const { id, purpose, remaining_balance, status } = loan;
  
    return (
      <div
        className="flexSpaceCenter gap10 p10 r8 lightBg pointer animate"
        onClick={() =>
          openBottomSheet({
            component: "tabs",
            title: "Loan Details",
            id,
            source: LOANS,
          })
        }
      >
        <div className="flexNullCenter gap8">
          <DocumentTextOutline fontSize={14} className="main" />
          <div>
            <div className="font12 semiBold textDark capitalize">
              {stringify(purpose)}
            </div>
            <div className="font11 grey">
              {currencyFormat(remaining_balance)} remaining
            </div>
          </div>
        </div>
        <StatusTag status={status} />
      </div>
    );
  };
  
  const DeviceOrderRow = ({ order }: { order: any }) => {
    const { id, device, delivery_status, deposit_paid, monthly_installment } =
      order || {};
  
    return (
      <div
        className="flexNullCenter gap10 p10 r8 lightBg pointer animate"
        onClick={() =>
          openBottomSheet({
            component: "details",
            title: "Device Order",
            source: DEVICE_ORDERS,
            id: order?.id,
            data: order,
            state: order,
            footer: OrderFooter,
          })
        }
      >
        <PhonePortraitOutline fontSize={14} className="main" />
        <div className="flexGrow">
          <div className="font12 semiBold textDark">{device?.name}</div>
          <div className="font11 grey">
            {currencyFormat(monthly_installment)}/mo
            {!deposit_paid && " · Deposit pending"}
          </div>
        </div>
        <StatusTag status={delivery_status} />
      </div>
    );
  };